// React and third-party libraries
import { yupResolver } from "@hookform/resolvers/yup";
import clsx from "clsx";
import { ArrowLeft } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useBlocker, useNavigate } from "react-router-dom";

// Agent module imports
import { checkAndRefreshUrl } from "@/modules/agent/utils/refreshUrl";

// Shared components and utilities
import ExitModal from "@/shared/components/custom/modals/ExitModal";
import { LoadingSpinner } from "@/shared/components/custom/spin";
import { ROUTES } from "@/shared/constants/routes";
import { addNotification } from "@/shared/states/notification";
import parsePromise from "@/shared/utils/parsePromise";

// Module-specific imports
import { intakeFormSchema, type IntakeFormValues } from "../../constants/form-validation";
import {
  getIntakeForm,
  saveIntakeForm,
  SaveIntakeFormQuestionPayload,
} from "../../requests/intake-form";
import { useSettingsHeaderStore } from "../../states/settings-header";
import QuestionList from "../create-questionnaire-screen/QuestionList";
import { deduplicateResponseChoices } from "../create-questionnaire-screen/questionTreeUtils";

const IntakeFormScreen = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isExitModalOpen, setIsExitModalOpen] = useState(false);
  const [invalidQuestionIds, setInvalidQuestionIds] = useState<Set<string>>(new Set());
  const questionRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const allowNavigationRef = useRef(false); // Flag to allow navigation after save

  // Store for header button
  const { setHandleSave, setIsSaving: setHeaderIsSaving, setIsDisabled } = useSettingsHeaderStore();

  // React Hook Form setup with Yup validation
  const methods = useForm<IntakeFormValues>({
    resolver: yupResolver(intakeFormSchema),
    defaultValues: {
      questions: [],
      totalFilesRequested: 0,
    },
    mode: "onSubmit", // Only validate when user tries to submit
    reValidateMode: "onChange", // After first submit, re-validate on change to clear errors
  });

  const { formState: { isDirty }, watch } = methods;
  const questions = watch("questions") || [];

  // Track unsaved changes
  useEffect(() => {
    setHasUnsavedChanges(isDirty);
  }, [isDirty]);

  // Sync loading and saving states with header store
  useEffect(() => {
    setHeaderIsSaving(isSaving);
    setIsDisabled(isSaving || isLoading);
  }, [isSaving, isLoading, setHeaderIsSaving, setIsDisabled]);

  // Block navigation if there are unsaved changes
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    // Don't block if we're staying on the same route
    if (currentLocation.pathname === nextLocation.pathname) {
      return false;
    }

    // Allow navigation if explicitly permitted (after save)
    if (allowNavigationRef.current) {
      return false;
    }

    // Block navigation only if there are unsaved changes
    return hasUnsavedChanges;
  });

  // Handle blocked navigation by showing ExitModal
  useEffect(() => {
    if (blocker.state === 'blocked') {
      setIsExitModalOpen(true);
    }
  }, [blocker.state]);

  // Prevent accidental browser closure when there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Only prevent navigation if there are unsaved changes
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Fetch questions from intake form URL
  const fetchQuestionsFromIntakeFormUrl = async (intakeFormUrl: string) => {
    try {
      // Use the same URL refresh logic as other parts of the app
      const validSignedUrl = await checkAndRefreshUrl(intakeFormUrl);

      // Fetch questions data directly from blob storage
      const response = await fetch(validSignedUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch questions: ${response.statusText}`);
      }

      const questionsListData = await response.json();
      return questionsListData;
    } catch (error) {
      console.error('Error fetching intake form questions:', error);
      addNotification({
        type: "error",
        title: "Failed to load Intake Form",
        message: "Could not fetch intake form data. Please try again.",
      });
      return [];
    }
  };

  // Load intake form data on mount
  useEffect(() => {
    const loadIntakeForm = async () => {
      setIsLoading(true);
      try {
        const [error, response] = await parsePromise(getIntakeForm());

        if (error) {
          console.error('Failed to get intake form:', error);
          addNotification({
            type: "error",
            title: "Failed to load Intake Form",
            message: error?.message || "Could not fetch intake form. Please try again.",
          });
          // Initialize with empty questions if no form exists
          methods.reset({
            questions: [],
            totalFilesRequested: 0
          });
          return;
        }

        if (response?.success && response.intake_form_url) {
          // Fetch questions from the URL
          const questionsList = await fetchQuestionsFromIntakeFormUrl(response.intake_form_url);

          if (questionsList && questionsList.length > 0) {
            // For intake form, all questions are root-level (no sub-questions)
            // question_number is just an integer (1, 2, 3, etc.)
            const questionsWithMapping = questionsList.map((question: any) => ({
              ...question,
              parent_question_id: null,
              parent_question_response_option_index: null,
              // Map max_number_of_files from backend to max_files for internal use
              max_files: question.max_number_of_files || question.max_files,
            }));

            // Calculate total files requested
            const totalFiles = questionsWithMapping
              .filter((q: any) => q.question_type === 'file_upload')
              .reduce((sum: number, q: any) => sum + (q.max_files || 0), 0);

            methods.reset({
              questions: questionsWithMapping,
              totalFilesRequested: totalFiles
            });
          } else {
            // No questions yet - initialize empty
            methods.reset({
              questions: [],
              totalFilesRequested: 0
            });
          }
        } else {
          // No intake form URL - initialize empty
          methods.reset({
            questions: [],
            totalFilesRequested: 0
          });
        }
      } catch (error) {
        console.error('Error loading intake form:', error);
        addNotification({
          type: "error",
          title: "Error",
          message: "An unexpected error occurred while loading the intake form.",
        });
        methods.reset({
          questions: [],
          totalFilesRequested: 0
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadIntakeForm();
  }, [methods]);

  // Helper to generate field_name from question text
  const generateFieldName = (questionText: string): string => {
    return questionText
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .substring(0, 50); // Limit length
  };

  // Build question number for intake form (simple sequential numbering)
  const buildQuestionNumber = (question: any, allQuestions: any[]): string => {
    // For intake form, just use the position in the array (1-indexed)
    const index = allQuestions.findIndex(q => q.id === question.id);
    return String(index + 1);
  };

  // Scroll to invalid question
  const scrollToInvalidQuestion = useCallback((questionId: string) => {
    const element = questionRefs.current.get(questionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  // Handle save (Done button)
  const handleSave = useCallback(async () => {
    const currentQuestions = methods.getValues("questions");

    // Check if there are no questions
    if (!currentQuestions || currentQuestions.length === 0) {
      addNotification({
        type: "error",
        message: "Get started by clicking \"Add Question\" to add fields to this form.",
      });
      return;
    }

    // Check if there are any changes to save
    if (!isDirty) {
      // No changes, just navigate back
      allowNavigationRef.current = true;
      navigate(`/${ROUTES.SETTINGS}`);
      return;
    }

    // Trigger form validation using Yup schema
    const isValid = await methods.trigger();

    if (!isValid) {
      // Get validation errors
      const errors = methods.formState.errors;
      const invalidIds = new Set<string>();

      // Check if errors.questions has actual question-level errors
      const hasQuestionFieldErrors = errors.questions && Array.isArray(errors.questions) &&
        errors.questions.some((qError: any) => qError && Object.keys(qError).length > 0);

      // Prioritize question field errors over file limit errors
      if (hasQuestionFieldErrors) {
        // Collect all invalid question IDs based on form errors
        if (errors.questions && Array.isArray(errors.questions)) {
          errors.questions.forEach((questionError: any, index: number) => {
            if (questionError && currentQuestions[index]) {
              invalidIds.add(currentQuestions[index].id);
            }
          });
        }

        // Find the first invalid question and scroll to it
        const firstInvalidQuestion = currentQuestions.find((q: any) => invalidIds.has(q.id));
        if (firstInvalidQuestion) {
          setInvalidQuestionIds(invalidIds);
          scrollToInvalidQuestion(firstInvalidQuestion.id);
        }

        addNotification({
          type: "error",
          message: "Please complete all questions. Questions must have question text and options (for choice-based questions).",
        });
        return;
      }

      // Only check file limit errors if there are no question field errors
      if (errors.totalFilesRequested) {
        // Mark all file upload questions as invalid to highlight them
        currentQuestions.forEach((q: any) => {
          if (q.question_type === 'file_upload') {
            invalidIds.add(q.id);
          }
        });

        // Find the first file upload question and scroll to it
        const firstFileUploadQuestion = currentQuestions.find((q: any) => invalidIds.has(q.id));
        if (firstFileUploadQuestion) {
          setInvalidQuestionIds(invalidIds);
          scrollToInvalidQuestion(firstFileUploadQuestion.id);
        }

        addNotification({
          type: "error",
          title: "Combined file limit exceeded",
          message: "Number of files allowed across all questions cannot exceed 50. Please readjust to proceed.",
        });
        return;
      }
      return;
    }

    // Clear any previous invalid highlights
    setIsSaving(true);
    setInvalidQuestionIds(new Set());

    try {

      // Build questions payload with question_number
      const questionsPayload: SaveIntakeFormQuestionPayload[] = currentQuestions.map((question: any) => {
        const questionNumber = buildQuestionNumber(question, currentQuestions);

        // Determine response_choices based on question type
        // For text field and file upload, response_choices should be empty
        const responseChoices = (question.question_type === 'free_text' || question.question_type === 'file_upload')
          ? []
          : deduplicateResponseChoices(question.response_choices);

        // Trim whitespace from question text
        const trimmedQuestion = question.question?.trim() || '';

        const payload: SaveIntakeFormQuestionPayload = {
          question_number: questionNumber,
          question: trimmedQuestion,
          field_name: question.field_name || generateFieldName(trimmedQuestion),
          question_type: question.question_type,
          required: question.required || false,
          is_dropdown: question.is_dropdown || false,
          hidden_from_user: question.hidden_from_user || false,
          response_choices: responseChoices,
        };

        // Add max_number_of_files for file_upload type
        if (question.question_type === 'file_upload') {
          payload.max_number_of_files = question.max_files || 5;
        }

        return payload;
      });

      const [error, response] = await parsePromise(
        saveIntakeForm({ questions: questionsPayload })
      );

      if (error) {
        console.error("Save intake form error:", error);
        addNotification({
          type: "error",
          message: error?.response?.data?.error_message || "Failed to save intake form. Please try again.",
        });
        return;
      }

      if (response?.success) {
        addNotification({
          type: "success",
          message: "Intake form updated successfully.",
        });

        // Allow navigation and reset unsaved changes flag
        allowNavigationRef.current = true;
        setHasUnsavedChanges(false);

        // Navigate back to settings page
        navigate(`/${ROUTES.SETTINGS}`);
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Save intake form error:", error);
      addNotification({
        type: "error",
        message: "An unexpected error occurred. Please try again later.",
      });
    } finally {
      setIsSaving(false);
    }
  }, [methods, isDirty, navigate, scrollToInvalidQuestion]);

  // Handle save and exit from modal
  const handleSaveAndExit = async () => {
    // Call the save handler
    await handleSave();
    setIsExitModalOpen(false);
  };

  // Handle exit without saving
  const handleExitWithoutSaving = useCallback(() => {
    setIsExitModalOpen(false);

    // Allow navigation and reset unsaved changes flag
    allowNavigationRef.current = true;
    setHasUnsavedChanges(false);

    // If navigation was blocked, proceed with it
    if (blocker.state === 'blocked') {
      blocker.proceed();
      return;
    }

    // Navigate back to settings page
    navigate(`/${ROUTES.SETTINGS}`);
  }, [navigate, blocker]);

  // Handle close (back button)
  const handleClose = useCallback(() => {
    // If there are unsaved changes, show ExitModal
    if (hasUnsavedChanges) {
      setIsExitModalOpen(true);
    } else {
      // Navigate back to settings page
      navigate(`/${ROUTES.SETTINGS}`);
    }
  }, [hasUnsavedChanges, navigate]);

  // Set up the save handler for the header button
  useEffect(() => {
    setHandleSave(handleSave);

    // Cleanup: remove handler when component unmounts
    return () => {
      setHandleSave(null);
    };
  }, [setHandleSave, handleSave]);

  return (
    <>
      <div className={`w-full h-full bg-[#FEFEFE] rounded-lg border border-[#E4E4E7] shadow-lg flex flex-col overflow-hidden relative ${isSaving ? 'pointer-events-none' : ''}`}>
        {/* Overlay during save */}
        {isSaving && (
          <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-50">
            <div className="flex flex-col items-center gap-3">
              <LoadingSpinner
                containerClassName="bg-transparent relative"
                className="mx-auto"
              />
              <p className="text-sm text-gray-600 font-medium">Saving intake form...</p>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="flex items-center px-6 py-4 border-b border-gray-200">
          {/* Left Section - Back button and title */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-gray-100 transition-colors"
              disabled={isSaving}
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <h1 className="text-sm font-medium text-gray-900">
              Intake Form
            </h1>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 min-h-0 p-4 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner
                containerClassName="bg-transparent relative"
                className="mx-auto"
              />
            </div>
          ) : (
            <>
              {/* Questions List Form */}
              <FormProvider {...methods}>
                <form className={clsx("overflow-hidden", {
                  "size-full": questions.length > 0,
                })}>
                  <QuestionList
                    invalidQuestionIds={invalidQuestionIds}
                    onQuestionEdit={(questionId) => {
                      setInvalidQuestionIds(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(questionId);
                        return newSet;
                      });
                    }}
                    questionRefs={questionRefs}
                    hideComments={true}
                    hideSubQuestionMapping={true}
                    showFileUploadType={true}
                  />
                </form>
              </FormProvider>
            </>
          )}
        </div>
      </div>

      {/* Exit Modal - shown when there are unsaved changes */}
      <ExitModal
        isOpen={isExitModalOpen}
        onClose={() => {
          setIsExitModalOpen(false);
          // If navigation was blocked, reset the blocker
          if (blocker.state === 'blocked') {
            blocker.reset();
          }
        }}
        onExitWithoutSaving={handleExitWithoutSaving}
        onSaveAndExit={handleSaveAndExit}
        title="Exit without saving changes?"
        description="Any unsaved changes to this intake form will be lost."
        isSaving={isSaving}
      />
    </>
  );
};

export default IntakeFormScreen;
